import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { FootageReview, TimestampMarker } from '../models/Review';
import { footageService } from './FootageService';

export class ReviewService {
  private static instance: ReviewService;
  private reviewsCollection = collection(db, 'reviews');

  private constructor() {}

  static getInstance(): ReviewService {
    if (!ReviewService.instance) {
      ReviewService.instance = new ReviewService();
    }
    return ReviewService.instance;
  }

  async createReview(
    companyId: string,
    footageId: string,
    userId: string,
    userName: string,
    notes: string,
    notesPlainText: string,
    timestamps?: TimestampMarker[],
    incidentLogId?: string
  ): Promise<FootageReview> {
    const reviewRef = doc(this.reviewsCollection);
    const now = new Date();

    const review: FootageReview = {
      id: reviewRef.id,
      companyId,
      footageId,
      incidentLogId,
      reviewedBy: userId,
      reviewedByName: userName,
      reviewedAt: now,
      notes,
      notesPlainText,
      timestamps,
      tags: [],
    };

    await setDoc(reviewRef, {
      ...review,
      reviewedAt: Timestamp.fromDate(review.reviewedAt),
    });

    // Increment review count on footage
    await footageService.incrementReviewCount(footageId);

    return review;
  }

  async getReview(reviewId: string): Promise<FootageReview | null> {
    const reviewRef = doc(this.reviewsCollection, reviewId);
    const reviewSnap = await getDoc(reviewRef);

    if (!reviewSnap.exists()) {
      return null;
    }

    const data = reviewSnap.data();
    return {
      ...data,
      id: reviewSnap.id,
      reviewedAt: data.reviewedAt.toDate(),
      pdfGeneratedAt: data.pdfGeneratedAt?.toDate(),
    } as FootageReview;
  }

  async getReviewsByFootage(footageId: string): Promise<FootageReview[]> {
    const q = query(
      this.reviewsCollection,
      where('footageId', '==', footageId),
      orderBy('reviewedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        reviewedAt: data.reviewedAt.toDate(),
        pdfGeneratedAt: data.pdfGeneratedAt?.toDate(),
      } as FootageReview;
    });
  }

  async getReviewsByCompany(companyId: string): Promise<FootageReview[]> {
    const q = query(
      this.reviewsCollection,
      where('companyId', '==', companyId),
      orderBy('reviewedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        ...data,
        id: doc.id,
        reviewedAt: data.reviewedAt.toDate(),
        pdfGeneratedAt: data.pdfGeneratedAt?.toDate(),
      } as FootageReview;
    });
  }

  async updateReview(
    reviewId: string,
    notes: string,
    notesPlainText: string,
    timestamps?: TimestampMarker[]
  ): Promise<void> {
    const reviewRef = doc(this.reviewsCollection, reviewId);
    await updateDoc(reviewRef, {
      notes,
      notesPlainText,
      timestamps,
    });
  }

  async updateRating(
    reviewId: string,
    rating: 'approved' | 'flagged' | 'requires-action'
  ): Promise<void> {
    const reviewRef = doc(this.reviewsCollection, reviewId);
    await updateDoc(reviewRef, { rating });
  }

  async setPdfUrl(reviewId: string, pdfUrl: string): Promise<void> {
    const reviewRef = doc(this.reviewsCollection, reviewId);
    await updateDoc(reviewRef, {
      pdfUrl,
      pdfGeneratedAt: Timestamp.fromDate(new Date()),
    });
  }

  async addTags(reviewId: string, tags: string[]): Promise<void> {
    const review = await this.getReview(reviewId);
    if (!review) throw new Error('Review not found');

    const updatedTags = [...new Set([...review.tags, ...tags])];
    const reviewRef = doc(this.reviewsCollection, reviewId);
    await updateDoc(reviewRef, { tags: updatedTags });
  }
}

// Singleton instance
export const reviewService = ReviewService.getInstance();
